import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { HiOutlineExclamation } from "react-icons/hi";
import sendEmail from '../../../../Common/email/sendEmail';
import { AdminGetUser } from '../../../../Common/lib/Admin/AdminAPI';

/**
 * Displays the project accept confirmation modal
 * @returns Modal Component
 */
export default function AcceptModal({ openAcceptModal, setOpenAcceptModal, updateProjectInfo, projectInfo }) {
  const [buttonEnabled, setButtonEnabled] = useState(true);
  
  /**
   * Handles project approval. Sends email to the publisher of the approved project informing them that their project has been approved.
   */
  async function acceptHandling() {
    setButtonEnabled(false);

    // Get publisher user ID from createdByUserId field (Django user ID)
    const publisherUserId = projectInfo.createdByUserId || projectInfo.createdBy;

    // Project info doesn't contain publisher's email so we need to retrieve it from the user
    const result = await AdminGetUser(publisherUserId);
    let userObject = {
      email: ""
    };

    // The result from Cognito has all the user attributes but they are objects in an array so it maps through the array and assigns each attrbute to a property on the userObject object
    result.UserAttributes.map(x => {
      userObject[`${x.Name}`] = x.Value;
    });

    // Check that the destination email was populated
    if (userObject.email !== "" && userObject.email !== undefined) {
      let emailSubject = "Project Submission Approved"
      let emailBody = `Hello ${userObject.given_name}, \n\nWe are pleased to inform you that your research project data submission titled "${projectInfo.title}" has been approved and is now available on our website. Thank you for submitting your work and contributing to our platform. \n\nYou can find your uploaded work here: https://database.arcticfocus.org/metadata-display/${projectInfo.id}`;
      await sendEmail(userObject.email, emailSubject, emailBody, "Authorized");
    }

    // Handle closing modal and updating project. 
    // Since the button that opened this modal already changed the projectStatus to published, we just need to update the project in the table with updateProjectInfo().
    setOpenAcceptModal(false);
    updateProjectInfo();
  }

  return (
    <Transition.Root show={openAcceptModal} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={setOpenAcceptModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div>
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <HiOutlineExclamation className="h-6 w-6 text-green-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      Are you sure you want to accept this submission?
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        This submission will be public and searchable on the data catalogue.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    data-cy="approve-project"
                    disabled={!buttonEnabled}
                    className="mb-2 inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    /* Since the button that opened this modal already changed the projectStatus to published, we just need to update the project in the table  */
                    onClick={() => { acceptHandling(); }}
                  >
                    {buttonEnabled ? 
                      (
                      "Accept Submission"
                      ) : 
                      (
                        <>
                          <svg aria-hidden="true" class="w-5 h-5 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                          </svg>
                          <span class="sr-only">Loading...</span>
                        </>
                      )
                    }
                  </button>
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-300 hover:bg-gray-400 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    onClick={() => setOpenAcceptModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}